import React from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Select,
  Space,
  Typography,
  Tag,
  Rate,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  CheckCircleOutlined,
  StarOutlined,
  NumberOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { CLINICAL_AREAS, TEMPLATE_OPTIONS } from "../useEvaluationWizard";

const { Title, Text } = Typography;

function iconFor(key) {
  if (key === "yes_no") return <CheckCircleOutlined />;
  if (key === "input_score") return <NumberOutlined />;
  return <StarOutlined />;
}

// Vive fuera del componente, asi que recibe `t` en vez de llamar al hook.
function previewFor(key, t) {
  if (key === "yes_no")
    return (
      <Space size={6} wrap>
        <Tag>{t("formato.si")}</Tag>
        <Tag>{t("formato.no")}</Tag>
      </Space>
    );
  if (key === "rating_1_3") return <Rate count={3} disabled value={0} />;
  if (key === "rating_1_5") return <Rate count={5} disabled value={0} />;
  if (key === "input_score") return <Tag>{t("formato.ejemploPuntuacion")}</Tag>;
  return <Tag>—</Tag>;
}

export function EvaluationWizardStep1TypeArea({ subtype, onPickTemplate }) {
  const { t } = useTranslation("evaluations");

  // El `value` de cada area es el dato que viaja a la BD; solo se traduce la
  // etiqueta que se ve en el desplegable.
  const areaOptions = CLINICAL_AREAS.map((area) => ({
    value: area.value,
    label: t(`asistente.areas.${area.labelKey}`),
  }));

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Title level={5} style={{ marginTop: 0 }}>
            {t("asistente.paso1.titulo")}
          </Title>
          <Text type="secondary">{t("asistente.paso1.ayuda")}</Text>

          <div style={{ marginTop: 12 }}>
            <Row gutter={[12, 12]}>
              {TEMPLATE_OPTIONS.map((opt) => {
                const selected = subtype === opt.key;
                return (
                  <Col key={opt.key} xs={24} sm={12}>
                    <Card
                      className={`revi-eval-wizard__templateCard ${selected ? "is-selected" : ""}`}
                      variant="outlined"
                      role="button"
                      tabIndex={0}
                      onClick={() => onPickTemplate(opt.key)}
                    >
                      <Space
                        orientation="vertical"
                        size={6}
                        style={{ width: "100%" }}
                      >
                        <Space size={10}>
                          <span className="revi-eval-wizard__templateIcon">
                            {iconFor(opt.key)}
                          </span>
                          <div style={{ flex: 1 }}>
                            <Text strong>
                              {t(`asistente.plantillas.${opt.key}.titulo`)}
                            </Text>
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {t(
                                  `asistente.plantillas.${opt.key}.descripcion`,
                                )}
                              </Text>
                            </div>
                          </div>
                        </Space>
                        <div className="revi-eval-wizard__templatePreview">
                          {previewFor(opt.key, t)}
                        </div>
                      </Space>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>

          <div style={{ marginTop: 16 }}>
            <Title level={5} style={{ marginTop: 0 }}>
              {t("campos.areaClinica")}
            </Title>
            <Form.Item
              name="clinicalArea"
              rules={[
                {
                  required: true,
                  message: t("asistente.validacion.areaClinica"),
                },
              ]}
            >
              <Select
                placeholder={t("asistente.paso1.placeholderArea")}
                options={areaOptions}
                style={{ maxWidth: 340 }}
              />
            </Form.Item>
          </div>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            className="revi-eval-wizard__previewCard"
            variant="outlined"
            size="small"
            title={
              <Space size={8}>
                <AppstoreOutlined />
                <Text strong>{t("formato.titulo")}</Text>
              </Space>
            }
          >
            <Space orientation="vertical" size={6} style={{ width: "100%" }}>
              <Text type="secondary">{subtype}</Text>
              {previewFor(subtype, t)}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
